import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ChangeEvent, Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useState } from "react";
import LoadingBar from "../LoadingBar/LoadingBar";
import { useSelector } from "react-redux";
import { selectUserInformation } from "../../State/Slices/UserInformationSlice";
import { ClientBillingReportSettings } from "../../Types/ClientBilling";
import { updateBillingReportNotificationSettings } from "../../Services/API/BillingReport";
import { MuiPickersUtilsProvider, TimePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { Close } from "@material-ui/icons";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";

const useStyles = makeStyles((theme) => ({
  settingsSection_notifications: {
    maxWidth: "70vw",
    width: "100%",
    borderRadius: "0.25em",
    border: "1px solid rgba(0,0,0,0.25)",
    padding: "10px 20px",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
    },
  },
  dialogCloseButton: {
    position: "absolute",
    right: 8,
    top: 8,
    color: theme.palette.grey[500],
  },
  dialog: {
    "& .MuiDialogContent-root": {
      padding: theme.spacing(2),
    },
    "& .MuiDialogActions-root": {
      padding: theme.spacing(1),
    },
  },
}));

interface BillingReportSettings {
  toName: string;
  toEmail: string;
  fromName: string;
  emailSubject: string;
  scheduledDay: number;
  scheduledTime: Date;
}

function validateSettingsData({
  toName,
  toEmail,
  fromName,
  emailSubject,
  scheduledDay,
  scheduledTime,
}: BillingReportSettings) {
  if (
    toName === "" ||
    toEmail === "" ||
    fromName === "" ||
    emailSubject === "" ||
    scheduledDay === 0 ||
    scheduledTime === null
  ) {
    return false;
  }
  return true;
}

interface UpdateBillingSettingsModalProps {
  savedSettings: ClientBillingReportSettings;
  loadingSettings: boolean;
  setLoadingSettings: Dispatch<SetStateAction<boolean>>;
  setShowUpdateSettingsError: Dispatch<SetStateAction<boolean>>;
  updateSettingsErrorMessage: string;
  setUpdateSettingsErrorMessage: Dispatch<SetStateAction<string>>;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  refreshSettings: () => void;
}

export default function UpdateBillingSettingsModal({
  savedSettings,
  loadingSettings,
  setLoadingSettings,
  setShowUpdateSettingsError,
  updateSettingsErrorMessage,
  setUpdateSettingsErrorMessage,
  setShowModal,
  refreshSettings,
}: UpdateBillingSettingsModalProps) {
  const classes = useStyles();
  const { "http://nasrecruitment.io/activateId": userId } = useSelector(selectUserInformation);

  const [toName, setToName] = useState(savedSettings.to?.length > 0 ? savedSettings.to[0].name : "");
  const [toEmail, setToEmail] = useState(savedSettings.to?.length > 0 ? savedSettings.to[0].email : "");
  const [fromName, setFromName] = useState(savedSettings.fromName ?? "");
  const [emailSubject, setEmailSubject] = useState(savedSettings.subject ?? "");
  const [scheduledDay, setScheduledDay] = useState(savedSettings.scheduledDay ?? 1);
  const [scheduledTime, setScheduledTime] = useState(
    savedSettings.scheduledTime !== null ? savedSettings.scheduledTime : new Date()
  );

  const [saveError, setSaveError] = useState(false);
  const [showInputError, setShowInputError] = useState(false);

  function checkInputError(currentInputData: BillingReportSettings) {
    if (validateSettingsData(currentInputData)) {
      setShowInputError(false);
    } else {
      setShowInputError(true);
    }
  }

  const handleToNameInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const currentInputData = {
        toName: e.target.value,
        toEmail,
        fromName,
        emailSubject,
        scheduledDay,
        scheduledTime,
      };
      checkInputError(currentInputData);
      setToName(e.target.value);
    },
    [emailSubject, fromName, scheduledDay, scheduledTime, toEmail]
  );

  const handleToEmailInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const currentInputData = {
        toName,
        toEmail: e.target.value,
        fromName,
        emailSubject,
        scheduledDay,
        scheduledTime,
      };
      checkInputError(currentInputData);
      setToEmail(e.target.value);
    },
    [emailSubject, fromName, scheduledDay, scheduledTime, toName]
  );

  const handleFromNameInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const currentInputData = {
        toName,
        toEmail,
        fromName: e.target.value,
        emailSubject,
        scheduledDay,
        scheduledTime,
      };
      checkInputError(currentInputData);
      setFromName(e.target.value);
    },
    [emailSubject, scheduledDay, scheduledTime, toEmail, toName]
  );

  const handleEmailSubjectInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const currentInputData = {
        toName,
        toEmail,
        fromName,
        emailSubject: e.target.value,
        scheduledDay,
        scheduledTime,
      };
      checkInputError(currentInputData);
      setEmailSubject(e.target.value);
    },
    [fromName, scheduledDay, scheduledTime, toEmail, toName]
  );

  const handleDayOfMonthInput = useCallback(
    (
      event: ChangeEvent<{
        name?: string | undefined;
        value: unknown;
      }>,
      child: ReactNode
    ) => {
      const currentInputData = {
        toName,
        toEmail,
        fromName,
        emailSubject,
        scheduledDay: Number(event.target.value),
        scheduledTime,
      };
      checkInputError(currentInputData);
      setScheduledDay(Number(event.target.value));
    },
    [emailSubject, fromName, scheduledTime, toEmail, toName]
  );

  const handleTimeOfDayInput = useCallback(
    (date: MaterialUiPickersDate) => {
      const currentInputData = {
        toName,
        toEmail,
        fromName,
        emailSubject,
        scheduledDay,
        scheduledTime: date ?? scheduledTime,
      };
      checkInputError(currentInputData);
      setScheduledTime(date ?? scheduledTime);
    },
    [emailSubject, fromName, scheduledDay, scheduledTime, toEmail, toName]
  );

  function handleCloseModal() {
    setShowModal(false);
    setLoadingSettings(false);
    setSaveError(false);
  }

  const saveReportSettings = useCallback(() => {
    const currentInputData = {
      toName,
      toEmail,
      fromName,
      emailSubject,
      scheduledDay,
      scheduledTime,
    };
    if (validateSettingsData(currentInputData)) {
      setShowInputError(false);
      setLoadingSettings((_) => true);
      setSaveError(false);
      updateBillingReportNotificationSettings({
        userId,
        toName,
        toEmail,
        fromName,
        subject: emailSubject,
        scheduledDay,
        scheduledTime,
      })
        .then((res) => {
          refreshSettings();
          handleCloseModal();
        })
        .catch((err) => {
          console.error(err);
          setSaveError(true);
          setShowUpdateSettingsError(true);
          setUpdateSettingsErrorMessage("Error saving settings");
        })
        .finally(() => {
          setLoadingSettings(false);
        });
    } else {
      setShowInputError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailSubject, fromName, scheduledDay, scheduledTime, toEmail, toName, userId]);

  useEffect(() => {
    if (savedSettings.scheduledTime !== null) {
      setScheduledTime(savedSettings.scheduledTime as Date);
    }
  }, [savedSettings.scheduledTime]);

  return (
    <>
      <DialogTitle>Billing Report Notification Settings</DialogTitle>
      <IconButton
        className={classes.dialogCloseButton}
        aria-label="close"
        onClick={() => setShowModal(false)}
      >
        <Close />
      </IconButton>
      <DialogContent dividers>
        <LoadingBar loading={loadingSettings} />
        <Grid container spacing={2} direction="row">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="To Name"
              name="toName"
              value={toName}
              onChange={handleToNameInput}
              error={showInputError && toName === ""}
            />
            {showInputError && toName === "" && (
              <FormHelperText style={{ color: "rgba(255,0,0,.7)" }}>To Name is required.</FormHelperText>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="To Email Address"
              name="toEmail"
              value={toEmail}
              onChange={handleToEmailInput}
              error={showInputError && toEmail === ""}
            />
            {showInputError && toEmail === "" && (
              <FormHelperText style={{ color: "rgba(255,0,0,.7)" }}>
                To Email Address is required.
              </FormHelperText>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="From Name"
              name="fromName"
              value={fromName}
              onChange={handleFromNameInput}
              error={showInputError && fromName === ""}
            />
            {showInputError && fromName === "" && (
              <FormHelperText style={{ color: "rgba(255,0,0,.7)" }}>From Name is required.</FormHelperText>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Subject"
              name="emailSubject"
              value={emailSubject}
              onChange={handleEmailSubjectInput}
              error={showInputError && emailSubject === ""}
            />
            {showInputError && emailSubject === "" && (
              <FormHelperText style={{ color: "rgba(255,0,0,.7)" }}>Subject is required.</FormHelperText>
            )}
          </Grid>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Grid item xs={12} sm={6}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel htmlFor="scheduledDay">Day of Month</InputLabel>
                <Select
                  id="scheduledDay"
                  label="Day of Month"
                  name="scheduledDay"
                  value={scheduledDay}
                  onChange={handleDayOfMonthInput}
                  defaultValue={1}
                  error={showInputError && (scheduledDay <= 0 || scheduledDay > 30)}
                >
                  {[...Array(30).keys()] // 30 days in a month
                    .map((i) => i + 1)
                    .map((i) => (
                      <MenuItem key={i} value={i}>
                        {i}
                      </MenuItem>
                    ))}
                </Select>
                {showInputError && (scheduledDay <= 0 || scheduledDay > 30) && (
                  <FormHelperText style={{ color: "rgba(255,0,0,.7)" }}>
                    Day of month must be between 1 and 30.
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl style={{ width: "100%" }}>
                <TimePicker
                  id="scheduledTime"
                  value={scheduledTime}
                  label="Time of Day (EST/EDT)"
                  format="h:mm a"
                  onChange={handleTimeOfDayInput}
                  error={showInputError && scheduledTime == null}
                />
                {showInputError && scheduledTime == null && (
                  <FormHelperText style={{ color: "rgba(255,0,0,.7)" }}>
                    Time of day is required.
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          </MuiPickersUtilsProvider>
        </Grid>
      </DialogContent>
      <DialogActions>
        {showInputError && (
          <FormHelperText style={{ color: "rgba(255,0,0,.7)" }}>Error in one or more fields.</FormHelperText>
        )}
        <Button
          style={{
            backgroundColor: loadingSettings || showInputError ? "rgba(0,0,0,0.15)" : "green",
            color: loadingSettings || showInputError ? "gray" : "white",
          }}
          color={loadingSettings || showInputError ? "default" : "primary"}
          type="submit"
          variant="outlined"
          disabled={loadingSettings || showInputError}
          onClick={saveReportSettings}
        >
          Save Settings
        </Button>
        {saveError && (
          <FormHelperText style={{ color: "rgba(255,0,0,.7)" }}>{updateSettingsErrorMessage}</FormHelperText>
        )}
      </DialogActions>
    </>
  );
}
